import $ from 'jquery';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import ResolveSelectorConflictModalClass from 'notebook/plain-tables/selectors/ResolveSelectorConflictModal';
import SelectorDAO from 'notebook/plain-tables/selectors/SelectorDAO';
import {Cell, PastedCell} from 'notebook/tables/constants/CellConstants';
import {Selector} from 'notebook/tables/excess/EvaluationConstants';
import {Optional} from 'util/Types';

/**
 * Prompt the user to do "merge conflict resolution" on selectors from pasted items with names matching
 * existing ones but with different options.
 *
 * This handles the case where entire note parts with tables are being copied from one entry to another.
 *
 * @modifies {pastedTables} - updates selectorId on cells to use the existing ones.
 * @returns {Promise<Selector>} - the resolved, full list of selectors for the document.
 *
 */
export function resolveSelectorsFromPastedItems({
  pastedTables,
  pastedSelectors,
  existingSelectors,
}: {
  pastedTables: Array<Array<Array<Cell>>>;
  pastedSelectors: Array<Selector>;
  existingSelectors: Array<Selector>;
}): Promise<Array<Selector> | unknown> {
  return _resolveSelectorsFromPaste(pastedTables, existingSelectors, (cell: Cell) =>
    cell.selectorId ? _.findWhere(pastedSelectors, {staticId: cell.selectorId})! : null
  );
}

/**
 * Prompt the user to do "merge conflict resolution" on selectors from pasted tables with names matching
 * existing ones but with different options.
 *
 * This handles the cases where selectors are being parsed out of HTML, which includes:
 *  - copying a region of a table and pasting it inside of a table in annother entry
 *  - copying a region of a table and pasting as a new table in  another entry
 *  - copying note parts with tables from an entry into the procedure section of a protocol.
 *
 * @modifies {pastedTables} - updates selectorId on cells to use the existing ones.
 * @returns {Promise<Selector>} - the resolved, full list of selectors for the document.
 *
 */
export function resolveSelectorsFromPastedHTMLTables({
  pastedTables,
  existingSelectors,
  selectorsEnabled,
}: {
  pastedTables: Array<Array<Array<PastedCell>>>;
  existingSelectors: Array<Selector>;
  selectorsEnabled: boolean;
}): Promise<Array<Selector> | null> {
  return _resolveSelectorsFromPaste<PastedCell>(pastedTables, existingSelectors, (pastedCell) => {
    const {selector} = pastedCell;
    delete pastedCell.selector;
    return selectorsEnabled ? selector : null;
  });
}

/**
 * Mutate the input pastedTable to either strip / resolve selectors on the cell.
 * @return {Promise<array?>} - selectors to update
 */
async function _resolveSelectorsFromPaste<TCell>(
  pastedTables: Array<Array<Array<TCell & {selectorId?: Optional<string>}>>>,
  currentSelectors: Array<Selector>,
  extractSelector: (cell: TCell) => Optional<Selector>
): Promise<Array<Selector> | null> {
  const selectorsToCreate: Array<Selector> = [];
  const conflictingSelectors: Array<{name: string; staticId: string; pasteOptions: Array<string>}> = [];
  const conflictingSelectorIds: {[staticId: string]: boolean} = {};

  for (const pastedTable of pastedTables) {
    for (const pastedRow of pastedTable) {
      for (const pastedCell of pastedRow) {
        const cellSelector = extractSelector(pastedCell);
        if (cellSelector == null) {
          continue;
        }
        const {name, options} = cellSelector;
        // Check to see if an identical selector has already been created for the table or already exists on the document.
        const matchingSelector = _.findWhere([...currentSelectors, ...selectorsToCreate], {name});

        pastedCell.selectorId = (() => {
          // If we're trying to create a selector of the same name of a selector that already exists on the location document or that we've already created.
          if (matchingSelector != null) {
            const matchingStaticId = matchingSelector.staticId;
            const optionsEqual = isEqual(matchingSelector.options, options);
            // The current cell's selector has different options than a different selector of the same name, but is not already tracked as a conflicting selector.
            if (!optionsEqual && conflictingSelectorIds[matchingStaticId] == null) {
              conflictingSelectorIds[matchingStaticId] = true;
              conflictingSelectors.push({
                name: matchingSelector.name,
                staticId: matchingStaticId,
                pasteOptions: options,
              });
            }
            return matchingStaticId;
          } else {
            // If we're creating a selector that doesn't already exist on the document and that we aren't already trying to add from a previous loop iteration.
            const newSelector = SelectorDAO.makeSelector(name, options);
            selectorsToCreate.push(newSelector);
            return newSelector.staticId;
          }
        })();
      }
    }
  }

  if (conflictingSelectors.length === 0 && selectorsToCreate.length === 0) {
    return null; // No changes to selectors are required
  }

  const resolvedSelectors =
    conflictingSelectors.length === 0
      ? currentSelectors
      : await _resolveSelectorConflicts(currentSelectors, conflictingSelectors);
  // TODO: Figure out if this ! is safe.
  return [...resolvedSelectors!, ...selectorsToCreate];
}

// Returns a promise, which will be:
//   rejected if user cancels the operation (closes the modal)
//   resolved with fixed selectors
function _resolveSelectorConflicts(
  selectors: Array<Selector>,
  conflictingSelectors: Array<{name: string; staticId: string; pasteOptions: Array<string>}>
): JQueryPromise<Array<Selector>> {
  const deferredSelectors: JQueryPromise<Array<Selector>> = $.Deferred();
  ResolveSelectorConflictModalClass.open({
    selectors,
    conflictingSelectors,
    deferredSelectors,
  });
  return deferredSelectors.promise();
}
